import { AccordionInterface } from "../../interfaces/interfaces";
import { TFunction } from "i18next";
import { ModulesTagName, SubModulesTagName } from "@me-team/common/src/utils/enums";
import { ModuleProxy } from "@me-team/host/main/graphql/types";
import LogbookBranch from "../../images/branches/logbook-branch.png";
import EmployeeBranch from "../../images/branches/employee-branch.png";
import ServiceBranch from "../../images/branches/service-branch.png";
import ClientsBranch from "../../images/branches/clients-branch.png";
import OnlineBranch from "../../images/branches/online-branch.png";
import TariffBranch from "../../images/branches/tariff-branch.png";
import WarehouseBranch from "../../images/branches/warehouse-branch.png";
import MenuIcon from "@me-team/common/src/images/icones/dish.svg";
import MenuBranch from "../../images/branches/menu-branch.png";
import DishesBranch from "../../images/branches/dishes-branch.png";
import MeBooking from "../../images/branches/me-booking.png";
import MeBookingIconPath from "@me-team/common/src/images/icones/table-bar.svg";
import { getSidebarAccess } from "./sidebarAccess";

const { WAREHOUSES, PERSONNEL, SERVICES, LOGBOOK, CLIENTS, BOOKING, Menu } = ModulesTagName;
const { VISIT_HISTORY, SALARY, REVIEWS, STATISTIC } = SubModulesTagName;

class SidebarService {
    modulesList: ModuleProxy[];
    constructor(modulesList: ModuleProxy[]) {
        this.modulesList = modulesList;
    }

    private isModuleActive = (name: string, childName: string = null): boolean => {
        const parent = this.modulesList.find((module) => module.name === name);
        if (!parent) {
            return false;
        }
        if (!childName) {
            return parent.active;
        }

        return parent.childModules.find((module) => module.name === childName)?.active;
    };
    public accordionContentService = (
        currentBranchId: string,
        currentCompanyId: string,
        translate: TFunction,
        shouldBypassSidebarAccessFilter = false
    ): AccordionInterface[] => {
        const shouldShowByAccess = (hasAccess: boolean) => shouldBypassSidebarAccessFilter || hasAccess;
        const {
            hasModeratorRole,
            canAccessServiceListRoot,
            canAccessServiceCategoriesRoot,
            canCreateService,
            hasServicesRootAccess,
            canAccessClientListRoot,
            canCreateClient,
            canAccessClientHistoryRoot,
            canAccessEmployeeListRoot,
            canCreateEmployee,
            canAccessPositionListRoot,
            canAccessWorkScheduleRoot,
            canAccessSalaryRoot,
            canAccessPaymentsHistoryRoot,
            canAccessOnlineBookingSettingsRoot,
            canAccessReviewsRoot,
            hasOnlineBookingRootAccess,
            canAccessLogbookJournal,
            canCreateLogbookRecord,
            canAccessBookingStatistic,
            canAccessWarehouseListRoot,
            canAccessWarehouseOperationsRoot,
            canAccessProductsRoot,
            canCreateProductRoot,
            canAccessTechMapsRoot,
            canAccessWarehouseReportsRoot,
            canAccessProductCategoriesRoot,
            canAccessTechMapCategoriesRoot,
            hasWarehouseRootAccess,
            canAccessTariffModulesManageRoot,
            hasTariffManagementRootAccess,
            canAccessAccessSettingsRoot,
            canAccessMenuListRoot,
            canAccessMenuDishListRoot,
            canAccessMenuDishCategoryListRoot,
            canAccessMenuOptionsAndSupplementsListRoot,
            hasMenuDishesRootAccess,
        } = getSidebarAccess();
        const warehouseItems = [
            shouldShowByAccess(canAccessWarehouseListRoot) && {
                label: translate("Warehouses"),
                to: `warehouses/branch/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessWarehouseOperationsRoot) && {
                label: translate("Warehouse operations"),
                to: `warehouses/operations/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessProductsRoot) && {
                label: translate("Products"),
                to: `warehouse/product/${currentCompanyId}/branch/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canCreateProductRoot) && {
                label: translate("Add product"),
                to: `warehouse/${currentCompanyId}/new-product/branch/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessTechMapsRoot) && {
                label: translate("Technological maps"),
                to: `warehouse/technological-maps/${currentCompanyId}/branch/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessWarehouseReportsRoot) && {
                label: translate("Reports"),
                to: `warehouse/reports/branch/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessBookingStatistic) && {
                label: translate("Sales statistics"),
                to: `warehouses/sales-statistics/branch/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessProductCategoriesRoot) && {
                label: translate("Product categories"),
                to: `warehouse/product-categories/${currentCompanyId}/branch/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessTechMapCategoriesRoot) && {
                label: translate("Technological map categories"),
                to: `warehouse/technological-map-categories/${currentCompanyId}/branch/${currentBranchId}`,
                active: true,
            },
        ].filter(Boolean);
        const personnelItems = [
            shouldShowByAccess(canAccessEmployeeListRoot) && {
                label: translate("Employees"),
                to: `employees-list/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canCreateEmployee) && {
                label: translate("Add an employee"),
                to: `employees-list/add-employee/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessPositionListRoot) && {
                label: translate("Positions"),
                to: `positions/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessWorkScheduleRoot) && {
                label: translate("Work schedule"),
                to: `work-schedule/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessSalaryRoot) && {
                label: translate("Salary"),
                to: `personnel/${currentCompanyId}/salary/${currentBranchId}`,
                active: this.isModuleActive(PERSONNEL, SALARY),
            },
            shouldShowByAccess(canAccessPaymentsHistoryRoot) && {
                label: translate("History of Payments"),
                to: `personnel/${currentCompanyId}/payment-history/${currentBranchId}`,
                active: this.isModuleActive(PERSONNEL, SALARY),
            },
        ].filter(Boolean);
        const logbookItems = [
            shouldShowByAccess(canAccessLogbookJournal) && {
                label: translate("Logbook"),
                to: `logbook/journal/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canCreateLogbookRecord) && {
                label: translate("Add record"),
                to: `logbook/add-record/${currentBranchId}`,
                active: true,
            },
            shouldShowByAccess(canAccessBookingStatistic) && {
                label: translate("Booking Statistic"),
                to: `logbook/statistic/${currentBranchId}`,
                active: this.isModuleActive(LOGBOOK, STATISTIC),
            },
        ].filter(Boolean);

        return [
            ...(shouldShowByAccess(hasWarehouseRootAccess)
                ? [
                      {
                          title: translate("Warehouse accounting"),
                          active: this.isModuleActive(WAREHOUSES),
                          inactiveLink: `payment/company/${currentCompanyId}/modules/details/warehouses/branch/${currentBranchId}`,
                          items: warehouseItems,
                          icon: "bi bi-shop-window",
                      },
                  ]
                : []),
            ...(personnelItems.length > 0
                ? [
                      {
                          title: translate("Personnel management"),
                          active: this.isModuleActive(PERSONNEL),
                          items: personnelItems,
                          icon: "bi-layout-text-window-reverse",
                      },
                  ]
                : []),
            ...(shouldShowByAccess(hasServicesRootAccess)
                ? [
                      {
                          title: translate("Services"),
                          active: this.isModuleActive(SERVICES),
                          items: [
                              shouldShowByAccess(canAccessServiceListRoot) && {
                                  label: translate("Service list"),
                                  to: `services/service/${currentCompanyId}/service-list/${currentBranchId}`,
                                  active: true,
                              },
                              shouldShowByAccess(canCreateService) && {
                                  label: translate("Add a service"),
                                  to: `services/service/${currentCompanyId}/new/${currentBranchId}`,
                                  active: true,
                              },
                              shouldShowByAccess(canAccessServiceCategoriesRoot) && {
                                  label: translate("Service Categories"),
                                  to: `service-categories/${currentBranchId}`,
                                  active: true,
                              },
                          ],
                          icon: "bi-clipboard",
                      },
                  ]
                : []),
            ...(logbookItems.length > 0
                ? [
                      {
                          title: translate("Logbook"),
                          active: this.isModuleActive(LOGBOOK),
                          items: logbookItems,
                          icon: "bi-journals",
                      },
                  ]
                : []),
            ...(shouldShowByAccess(canAccessClientListRoot)
                ? [
                      {
                          title: translate("Clients"),
                          active: this.isModuleActive(CLIENTS),
                          items: [
                              shouldShowByAccess(canAccessClientListRoot) && {
                                  label: translate("List of Clients"),
                                  to: `clients/${currentCompanyId}/list/${currentBranchId}`,
                                  active: true,
                              },
                              shouldShowByAccess(canCreateClient) && {
                                  label: translate("Add client"),
                                  to: `clients/${currentCompanyId}/new/${currentBranchId}`,
                                  active: true,
                              },
                              shouldShowByAccess(canAccessClientHistoryRoot) && {
                                  label: translate("History of visits"),
                                  to: `clients-history-visit/${currentBranchId}`,
                                  active: this.isModuleActive(CLIENTS, VISIT_HISTORY),
                              },
                          ],
                          icon: "bi-people",
                      },
                  ]
                : []),
            ...(shouldShowByAccess(canAccessMenuListRoot)
                ? [
                      {
                          title: translate("menu"),
                          active: this.isModuleActive(Menu),
                          inactiveLink: `payment/company/${currentCompanyId}/modules/details/menu/branch/${currentBranchId}`,
                          items: [
                              {
                                  label: translate("All menu"),
                                  to: `menu/all-menus/branch/${currentBranchId}`,
                                  active: true,
                              },
                          ],
                          icon: "bi-book",
                      },
                  ]
                : []),

            ...(shouldShowByAccess(hasMenuDishesRootAccess)
                ? [
                      {
                          title: translate("Dishes"),
                          active: this.isModuleActive(Menu),
                          inactiveLink: `payment/company/${currentCompanyId}/modules/details/menu/branch/${currentBranchId}`,
                          items: [
                              shouldShowByAccess(canAccessMenuDishListRoot) && {
                                  label: translate("List of dishes"),
                                  to: `menu/dishes/branch/${currentBranchId}`,
                                  active: true,
                              },
                              shouldShowByAccess(canAccessMenuDishCategoryListRoot) && {
                                  label: translate("Category of dishes"),
                                  to: `menu/dishes/categories/branch/${currentBranchId}`,
                                  active: true,
                              },
                              shouldShowByAccess(canAccessMenuOptionsAndSupplementsListRoot) && {
                                  label: translate("Options and Add-ons for dishes"),
                                  to: `menu/dishes/add-ons/branch/${currentBranchId}`,
                                  active: true,
                              },
                          ].filter(Boolean),
                          iconPath: MenuIcon,
                      },
                  ]
                : []),
            ...(shouldShowByAccess(hasOnlineBookingRootAccess)
                ? [
                      {
                          title: translate("Online Booking"),
                          active: this.isModuleActive(BOOKING),
                          items: [
                              shouldShowByAccess(canAccessOnlineBookingSettingsRoot) && {
                                  label: translate("Online Booking Config"),
                                  to: `booking/company/${currentCompanyId}/config/${currentBranchId}`,
                                  active: true,
                              },
                              shouldShowByAccess(canAccessReviewsRoot) && {
                                  label: translate("Reviews"),
                                  to: `booking/review/company/${currentCompanyId}/${currentBranchId}`,
                                  active: this.isModuleActive(BOOKING, REVIEWS),
                              },
                          ],
                          icon: "bi-calendar4-event",
                      },
                  ]
                : []),
            ...(shouldShowByAccess(hasTariffManagementRootAccess)
                ? [
                      {
                          title: translate("Management of Tariffs"),
                          active: true,
                          items: [
                              {
                                  label: translate("Tariff Plans"),
                                  to: `payment/company/${currentCompanyId}/tariff/${currentBranchId}`,
                                  active: true,
                              },
                              ...(shouldShowByAccess(canAccessTariffModulesManageRoot)
                                  ? [
                                        {
                                            label: translate("Modules"),
                                            to: `payment/company/${currentCompanyId}/modules/branch/${currentBranchId}`,
                                            active: true,
                                        },
                                    ]
                                  : []),

                              {
                                  label: translate("Payment History"),
                                  to: `payment/history/company/${currentCompanyId}/${currentBranchId}`,
                                  active: true,
                              },
                          ],
                          icon: "bi-card-checklist",
                      },
                  ]
                : []),
            ...(hasModeratorRole
                ? [
                      {
                          title: translate("ME-Booking"),
                          active: true,
                          items: [
                              { label: translate("Booking"), to: `me-booking/restaurant-booking`, active: true },
                              {
                                  label: translate("Restaurant Request"),
                                  to: `me-booking/restaurant-request`,
                                  active: true,
                              },
                          ],
                          iconPath: MeBookingIconPath,
                      },
                  ]
                : []),
            ...(shouldShowByAccess(canAccessAccessSettingsRoot)
                ? [
                      {
                          title: translate("Access settings"),
                          active: true,
                          items: [
                              {
                                  label: translate("Manage settings"),
                                  to: `branch/access-settings/${currentBranchId}`,
                                  active: true,
                              },
                              {
                                  label: translate("Role Management"),
                                  to: `branch/role-management/${currentBranchId}`,
                                  active: true,
                              },
                          ],
                          icon: "bi-person-lock",
                      },
                  ]
                : []),
        ];
    };

    public getSections = (t: TFunction, currentBranchId: number | string, currentCompanyId: number | string) => {
        const {
            hasModeratorRole,
            canAccessServiceListRoot,
            canAccessServiceCategoriesRoot,
            canCreateService,
            hasServicesRootAccess,
            canAccessClientListRoot,
            canCreateClient,
            canAccessClientHistoryRoot,
            canAccessEmployeeListRoot,
            canCreateEmployee,
            canAccessPositionListRoot,
            canAccessWorkScheduleRoot,
            canAccessSalaryRoot,
            canAccessPaymentsHistoryRoot,
            canAccessOnlineBookingSettingsRoot,
            canAccessReviewsRoot,
            hasOnlineBookingRootAccess,
            canAccessLogbookJournal,
            canCreateLogbookRecord,
            canAccessBookingStatistic,
            canAccessLogbookRoot,
            canAccessWarehouseListRoot,
            canAccessWarehouseOperationsRoot,
            canAccessProductsRoot,
            canCreateProductRoot,
            canAccessTechMapsRoot,
            canAccessWarehouseReportsRoot,
            canAccessProductCategoriesRoot,
            canAccessTechMapCategoriesRoot,
            hasWarehouseRootAccess,
            canAccessTariffModulesManageRoot,
            hasTariffManagementRootAccess,
            canAccessMenuListRoot,
            canCreateMenu,
            canAccessMenuDishListRoot,
            canAccessMenuDishCategoryListRoot,
            canAccessMenuOptionsAndSupplementsListRoot,
            canCreateMenuDish,
            hasMenuRootAccess,
        } = getSidebarAccess();
        const warehouseItems = [
            canAccessWarehouseOperationsRoot && {
                label: t("Warehouse operations"),
                to: `/warehouses/operations/${currentBranchId}`,
                active: true,
            },
            canAccessProductsRoot && {
                label: t("Products"),
                to: `/warehouse/product/${currentCompanyId}/branch/${currentBranchId}`,
                active: true,
            },
            canCreateProductRoot && {
                label: t("Add product"),
                to: `/warehouse/${currentCompanyId}/new-product/branch/${currentBranchId}`,
                active: true,
            },
            canAccessTechMapsRoot && {
                label: t("Technological maps"),
                to: `/warehouse/technological-maps/${currentCompanyId}/branch/${currentBranchId}`,
                active: true,
            },
            canAccessWarehouseReportsRoot && {
                label: t("Reports"),
                to: `/warehouse/reports/branch/${currentBranchId}`,
                active: true,
            },
            canAccessBookingStatistic && {
                label: t("Sales statistics"),
                to: `/warehouses/sales-statistics/branch/${currentBranchId}`,
                active: true,
            },
            canAccessProductCategoriesRoot && {
                label: t("Product categories"),
                to: `/warehouse/product-categories/${currentCompanyId}/branch/${currentBranchId}`,
                active: true,
            },
            canAccessTechMapCategoriesRoot && {
                label: t("Technological map categories"),
                to: `/warehouse/technological-map-categories/${currentCompanyId}/branch/${currentBranchId}`,
                active: true,
            },
        ].filter(Boolean);
        const personnelItems = [
            canAccessEmployeeListRoot && {
                label: t("Employees"),
                to: `/employees-list/${currentBranchId}`,
                active: true,
            },
            canCreateEmployee && {
                label: t("Add an employee"),
                to: `/employees-list/add-employee/${currentBranchId}`,
                active: true,
            },
            canAccessPositionListRoot && { label: t("Positions"), to: `/positions/${currentBranchId}`, active: true },
            canAccessWorkScheduleRoot && {
                label: t("Work schedule"),
                to: `/work-schedule/${currentBranchId}`,
                active: true,
            },
            canAccessSalaryRoot && {
                label: t("Salary"),
                to: `/personnel/${currentCompanyId}/salary/${currentBranchId}`,
                active: this.isModuleActive(PERSONNEL, SALARY),
            },
            canAccessPaymentsHistoryRoot && {
                label: t("History of Payments"),
                to: `/personnel/${currentCompanyId}/payment-history/${currentBranchId}`,
                active: this.isModuleActive(PERSONNEL, SALARY),
            },
        ].filter(Boolean);
        const logbookItems = [
            canAccessLogbookJournal && {
                label: t("Logbook"),
                to: `/logbook/journal/${currentBranchId}`,
                active: true,
            },
            canAccessBookingStatistic && {
                label: t("Booking Statistic"),
                to: `/logbook/statistic/${currentBranchId}`,
                active: this.isModuleActive(LOGBOOK, STATISTIC),
            },
        ].filter(Boolean);
        return [
            ...(hasWarehouseRootAccess
                ? [
                      {
                          title: t("Warehouse accounting"),
                          linkLabel: t("Warehouses"),
                          navigateTo: `/warehouses/branch/${currentBranchId}`,
                          active: this.isModuleActive(WAREHOUSES),
                          items: warehouseItems,
                          image: WarehouseBranch,
                          isHidden: !canAccessWarehouseListRoot,
                      },
                  ]
                : []),
            ...(canAccessMenuDishListRoot
                ? [
                      {
                          title: t("Dishes"),
                          linkLabel: t("List of dishes"),
                          active: this.isModuleActive(Menu),
                          navigateTo: `/menu/dishes/branch/${currentBranchId}`,
                          disabledNavigateTo: `/payment/company/${currentCompanyId}/modules/details/menu/branch/${currentBranchId}`,
                          items: [
                              canCreateMenuDish && {
                                  label: t("Add dish"),
                                  to: `/menu/dishes/create-dish/branch/${currentBranchId}`,
                                  active: true,
                              },
                              canAccessMenuDishCategoryListRoot && {
                                  label: t("Category of dishes"),
                                  to: `/menu/dishes/categories/branch/${currentBranchId}`,
                                  active: true,
                              },
                              canAccessMenuOptionsAndSupplementsListRoot && {
                                  label: t("Options and Add-ons for dishes"),
                                  to: `/menu/dishes/add-ons/branch/${currentBranchId}`,
                                  active: true,
                              },
                          ],
                          image: DishesBranch,
                      },
                  ]
                : []),
            ...(hasMenuRootAccess
                ? [
                      {
                          title: t("menu"),
                          linkLabel: t("All menu"),
                          active: this.isModuleActive(Menu),
                          navigateTo: `/menu/all-menus/branch/${currentBranchId}`,
                          disabledNavigateTo: `/payment/company/${currentCompanyId}/modules/details/menu/branch/${currentBranchId}`,
                          items: [
                              canCreateMenu && {
                                  label: t("Create menu"),
                                  to: `/menu/all-menus/branch/${currentBranchId}`,
                                  active: true,
                              },
                          ],
                          image: MenuBranch,
                          isHidden: !canAccessMenuListRoot,
                      },
                  ]
                : []),
            ...(canAccessLogbookRoot
                ? [
                      {
                          title: t("Logbook"),
                          linkLabel: t("Add record"),
                          navigateTo: `/logbook/add-record/${currentBranchId}`,
                          active: this.isModuleActive(LOGBOOK),
                          items: logbookItems,
                          image: LogbookBranch,
                          isHidden: !canCreateLogbookRecord,
                      },
                  ]
                : []),
            ...(personnelItems.length > 0
                ? [
                      {
                          title: t("Personnel management"),
                          linkLabel: t("Work schedule"),
                          active: this.isModuleActive(PERSONNEL),
                          navigateTo: `/work-schedule/${currentBranchId}`,
                          items: personnelItems,
                          image: EmployeeBranch,
                          isHidden: !canAccessWorkScheduleRoot,
                      },
                  ]
                : []),
            ...(hasServicesRootAccess
                ? [
                      {
                          title: t("Services"),
                          linkLabel: t("Service list"),
                          active: this.isModuleActive(SERVICES),
                          navigateTo: `/services/service/${currentCompanyId}/service-list/${currentBranchId}`,
                          items: [
                              canAccessServiceListRoot && {
                                  label: t("Service list"),
                                  to: `/services/service/${currentCompanyId}/service-list/${currentBranchId}`,
                                  active: true,
                              },
                              canCreateService && {
                                  label: t("Add a service"),
                                  to: `/services/service/${currentCompanyId}/new/${currentBranchId}`,
                                  active: true,
                              },
                              canAccessServiceCategoriesRoot && {
                                  label: t("Service Categories"),
                                  to: `/service-categories/${currentBranchId}`,
                                  active: true,
                              },
                          ],
                          image: ServiceBranch,
                          isHidden: !canAccessServiceListRoot,
                      },
                  ]
                : []),
            ...(canAccessClientListRoot
                ? [
                      {
                          title: t("Clients"),
                          linkLabel: t("Add client"),
                          active: this.isModuleActive(CLIENTS),
                          navigateTo: `/clients/${currentCompanyId}/new/${currentBranchId}`,
                          isHidden: !canCreateClient,
                          items: [
                              canAccessClientListRoot && {
                                  label: t("Client list"),
                                  to: `/clients/${currentCompanyId}/list/${currentBranchId}`,
                                  active: true,
                              },
                              canAccessClientHistoryRoot && {
                                  label: t("History of visits"),
                                  to: `/clients-history-visit/${currentBranchId}`,
                                  active: this.isModuleActive(CLIENTS, VISIT_HISTORY),
                              },
                          ],
                          image: ClientsBranch,
                      },
                  ]
                : []),
            ...(hasOnlineBookingRootAccess
                ? [
                      {
                          title: t("Online Booking"),
                          linkLabel: t("View"),
                          active: this.isModuleActive(BOOKING),
                          navigateTo: `/booking/company/${currentCompanyId}/config/${currentBranchId}`,
                          items: [
                              canAccessOnlineBookingSettingsRoot && {
                                  label: t("Online Booking Config"),
                                  to: `/booking/company/${currentCompanyId}/config/${currentBranchId}`,
                                  active: true,
                              },
                              canAccessReviewsRoot && {
                                  label: t("Reviews"),
                                  to: `/booking/review/company/${currentCompanyId}/${currentBranchId}`,
                                  active: this.isModuleActive(BOOKING, REVIEWS),
                              },
                          ],
                          image: OnlineBranch,
                          isHidden: !canAccessOnlineBookingSettingsRoot,
                      },
                  ]
                : []),
            ...(hasTariffManagementRootAccess
                ? [
                      {
                          title: t("Management of Tariffs"),
                          active: true,
                          linkLabel: t("Tariff Plans"),
                          navigateTo: `/payment/company/${currentCompanyId}/tariff/${currentBranchId}`,
                          items: [
                              {
                                  label: t("Tariff Plans"),
                                  to: `/payment/company/${currentCompanyId}/tariff/${currentBranchId}`,
                                  active: true,
                              },

                              ...(canAccessTariffModulesManageRoot
                                  ? [
                                        {
                                            label: t("Modules"),
                                            to: `/payment/company/${currentCompanyId}/modules/branch/${currentBranchId}`,
                                            active: true,
                                        },
                                    ]
                                  : []),

                              {
                                  label: t("Payment History"),
                                  to: `/payment/history/company/${currentCompanyId}/${currentBranchId}`,
                                  active: true,
                              },
                          ],
                          image: TariffBranch,
                      },
                  ]
                : []),
            ...(hasModeratorRole
                ? [
                      {
                          title: t("ME-Booking"),
                          linkLabel: t("View"),
                          active: true,
                          navigateTo: `/me-booking/restaurant-booking`,
                          items: [
                              { label: t("Booking"), to: `/me-booking/restaurant-booking`, active: true },
                              { label: t("Restaurant Request"), to: `/me-booking/restaurant-request`, active: true },
                          ],
                          image: MeBooking,
                      },
                  ]
                : []),
        ];
    };
}

export default SidebarService;
